/* eslint-disable react/prop-types */
import { CustomTooltip } from '@hi/brabus'
import { Box, Stack, Typography } from '@mui/material'
import { Card } from 'components/CustomCard'
import { currencyFormatter, floatNumberFormatter, integerNumberFormatter, percentageFormatter } from 'utils/formatters'

const returnColor = (value) => {
  if (value < 0) return '#EF3E39'
  if (value === 0) return '#666'
  return '#0B79D0'
}

/**
 * This function renders a set of cards displaying various metrics related to a conversion report.
 *
 * @param {Object} props - The component's props.
 * @param {Object} props.reportData - An object containing the report data.
 * @param {number} props.reportData.TotalClicksToMessage - The total number of conversations initiated.
 * @param {number} props.reportData.TotalCost - The total cost of the campaigns.
 * @param {number} props.reportData.AverageConversationCost - The Average of conversation cost of the campaigns.
 * @param {number} props.reportData.TotalImpressions - The total number of impressions.
 * @param {number} props.reportData.TotalReach - The total number of people reached.
 * @param {number} props.reportData.TotalSpend - The total amount spent on campaigns.
 * @param {number} props.reportData.TotalClicks - The total of clicks on campaigns.
 * @param {number} props.reportData.TotalSales - The total of sales on campaigns.
 * @param {number} props.reportData.TotalSalesAmount - The total amount of sales on campaigns.
 * @param {number} props.reportData.Cpa - The cost by aquisition on campaing.
 * @param {number} props.reportData.Roi - The return of investiment on campaing.
 *
 * @returns {JSX.Element} - The rendered component.
 */
function ConversionReportCards({
  reportData: {
    TotalClicksToMessage,
    AverageConversationCost,
    TotalImpressions,
    TotalReach,
    TotalSpend,
    TotalClicks,
    TotalSales,
    TotalSalesAmount,
    Cpa,
    Roi,
  },
}) {
  const percentageConverted = TotalClicksToMessage > 0 ? (TotalSales * 100) / TotalClicksToMessage : 0

  const styles = {
    textAlign: 'center',
    padding: '0 1rem',
  }

  return (
    <>
      <Stack
        component="section"
        id="conversion-report-cards"
        data-testid="conversion-report-cards-cost-results"
        flexDirection="row"
        alignItems="center"
        justifyContent={{ xl: 'center' }}
        flexWrap={{ md: 'wrap', xl: 'nowrap' }}
        mt="2rem"
        gap={{ xs: '1rem', md: '2rem' }}
      >
        <Box mt="1rem">
          <Typography variant="h6" color="GrayText">
            Investimentos e custos
          </Typography>
          <Stack
            component="section"
            id="conversion-report-cards"
            flexDirection="row"
            alignItems="center"
            justifyContent={{ xl: 'center' }}
            mt="2rem"
            gap={{ xs: '1rem', md: '2rem' }}
          >
            <Card.Root data-testid="totalSpend" key="totalSpend">
              <Card.Title
                title="Investimento total nas campanhas"
                message="Soma dos investimentos (custo) de todas as campanhas no período selecionado."
              />
              <Card.Content amount={currencyFormatter.format(TotalSpend)} isLoading={false} sx={styles} />
            </Card.Root>
            <Card.Root data-testid="spendByChat" key="spendByChat">
              <Card.Title
                title="Custo médio por conversa"
                message="Valor médio gasto por conversa gerada no período selecionado."
              />
              <Card.Content amount={currencyFormatter.format(AverageConversationCost)} isLoading={false} sx={styles} />
            </Card.Root>
            <Card.Root data-testid="cpa" key="cpa">
              <Card.Title
                title="CPA - Custo por aquisição"
                message="Valor médio gasto por compra realizada no período."
              />
              <Card.Content amount={currencyFormatter.format(Cpa)} isLoading={false} sx={styles} />
            </Card.Root>
          </Stack>
        </Box>
        <Stack display="flex" flexDirection="column" mt="1rem">
          <Typography variant="h6" color="GrayText">
            Resultado e receita
          </Typography>
          <Stack
            component="section"
            id="conversion-report-cards"
            flexDirection="row"
            alignItems="center"
            justifyContent={{ xl: 'center' }}
            mt="2rem"
            gap={{ xs: '1rem', md: '2rem' }}
          >
            <Card.Root data-testid="salesAmount" key="salesAmount">
              <Card.Title
                title="Valor total de conversão de compras"
                message="Valor total da compras realizadas no período."
              />
              <Card.Content amount={currencyFormatter.format(TotalSalesAmount)} isLoading={false} sx={styles} />
            </Card.Root>
            <Card.Root data-testid="roi" key="roi">
              <Card.Title title="ROI" message="Retorno sobre investimento." />
              <Card.Content
                amount={`${percentageFormatter.format(Roi)}%`}
                isLoading={false}
                sx={{
                  ...styles,
                  color: returnColor(Roi),
                }}
              />
            </Card.Root>
          </Stack>
        </Stack>
      </Stack>
      <Stack
        component="section"
        id="conversion-report-cards"
        data-testid="conversion-report-cards-performance"
        flexDirection="row"
        alignItems="center"
        justifyContent={{ xl: 'center' }}
        gap={{ xs: '1rem', md: '2rem' }}
      >
        <Box mt="1rem">
          <Typography variant="h6" color="GrayText">
            Desempenho (funil)
          </Typography>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: '1rem', md: '2rem' }}>
            <Stack
              component="section"
              id="conversion-report-cards"
              flexDirection="row"
              flexWrap="wrap"
              alignItems="center"
              justifyContent={{ xl: 'center' }}
              mt="2rem"
              gap="2rem"
            >
              <Card.Root data-testid="totalReacts" key="totalReacts">
                <Card.Title
                  title="Impressões totais"
                  message="Total de impressões dos anúncios no período selecionado."
                />
                <Card.Content amount={integerNumberFormatter.format(TotalImpressions)} isLoading={false} sx={styles} />
              </Card.Root>

              <Card.Root data-testid="rangeTotal" key="rangeTotal">
                <Card.Title
                  title="Alcance total"
                  message="Total de pessoas alcançadas pelos anúncios no período selecionado."
                />
                <Card.Content amount={integerNumberFormatter.format(TotalReach)} isLoading={false} sx={styles} />
              </Card.Root>

              <Card.Root data-testid="clicksTotal" key="clicksTotal">
                <Card.Title title="Clicks totais" message="Total de cliques no seu anúncio no período selecionado." />
                <Card.Content amount={integerNumberFormatter.format(TotalClicks)} isLoading={false} sx={styles} />
              </Card.Root>

              <Stack display="flex" flexDirection="row" gap={{ xs: '1rem', md: '2rem' }}>
                <div
                  style={{
                    position: 'relative',
                  }}
                >
                  <Card.Root data-testid="initiatedChat" key="initiatedChat">
                    <Card.Title
                      title="Total de conversas iniciadas"
                      message="Quantidade total de conversas iniciadas a partir dos seus anúncios no período selecionado."
                    />
                    <Card.Content
                      amount={integerNumberFormatter.format(TotalClicksToMessage)}
                      isLoading={false}
                      sx={styles}
                    />
                  </Card.Root>

                  <span
                    style={{
                      position: 'absolute',
                      right: '-3rem',
                      top: '35%',
                      float: '',
                      minWidth: '4.5rem',
                      height: '1.5rem',
                      backgroundColor: '#00AEEF',
                      color: '#FFFFFF',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'left',
                      paddingLeft: 5,
                      borderRadius: '0.188rem 0.25rem 0.25rem 0.188rem',
                      boxShadow: '1px 1.5px 4px 0px rgba(170, 174, 181, 0.45)',
                      WebkitTransform: 'translateX(6px)',
                      transform: 'translateX(6px)',
                      cursor: 'pointer',
                    }}
                  >
                    <CustomTooltip
                      placement="top"
                      title="Taxa de conversão"
                      body="Percentual de conversão de uma etapa do funil para outra."
                    >
                      {floatNumberFormatter.format(percentageConverted)}%
                    </CustomTooltip>
                    <span
                      style={{
                        position: 'absolute',
                        right: '-0.45rem',
                        top: '0.25rem',
                        float: '',
                        width: '0.5rem',
                        height: '0.5rem',
                        backgroundColor: '#00AEEF',
                        borderTop: '0.5rem solid #00AEEF',
                        borderRight: '0.5rem solid #00AEEF',
                        borderRadius: '0 0.188rem 0 100%',
                        transform: 'rotate(45deg)',
                        cursor: 'pointer',
                      }}
                    />
                  </span>
                </div>

                <Card.Root data-testid="totalSales" key="totalSales">
                  <Card.Title
                    title="Total de compras"
                    message="Número total de compras realizadas no período selecionado."
                  />
                  <Card.Content amount={integerNumberFormatter.format(TotalSales)} isLoading={false} sx={styles} />
                </Card.Root>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Stack>
    </>
  )
}

export default ConversionReportCards
